import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { SHOP_TREATMENTS_URL } from "@/components/care/constants";
import { ReactComponent as Checkmark } from "@/components/common/icons/checkmark.svg";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import { EcommProduct } from "@/types/care";
import { AddCartItemPayload } from "@/types/cart";
import { cn } from "@/utils/cn";

export const MALE_PARTNER_TREATMENT_VOUCHER_SLUG =
  "male-partner-treatment-voucher";

const MalePartnerTreatmentVoucherCard: React.FC<{
  voucherProduct: EcommProduct;
  addToCart?: (items: AddCartItemPayload[]) => void;
  openCart?: () => void;
  inCart?: boolean;
}> = ({ voucherProduct, addToCart, inCart, openCart }) => {
  const currentUser = useLoggedInUser();
  const useNewPDP = currentUser?.features.newPDP;
  const navigate = useNavigate();
  const handleCardClick = useCallback(() => {
    if (useNewPDP) {
      navigate(
        `${SHOP_TREATMENTS_URL}/product/${MALE_PARTNER_TREATMENT_VOUCHER_SLUG}/`
      );
    } else {
      window.open(
        "/care/shop/male-partner-voucher/",
        "_blank",
        "noopener,noreferrer"
      );
    }
  }, [useNewPDP, navigate]);

  return (
    <div
      className="bg-white border-evvy-black border-opacity-10 border rounded-2xl w-full max-w-[250px] sm:max-w-[285px] flex flex-col cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-center rounded-2xl bg-evvy-dark-beige mb-7 relative">
        {/* FOR PARTNER label */}
        <div className="absolute top-3 left-3 z-10">
          <div className="uppercase text-sm font-semibold rounded-full border border-black border-opacity-5 px-3 py-0.5 bg-white text-black whitespace-nowrap">
            For Partner
          </div>
        </div>
        {voucherProduct.image_url ? (
          <img
            src={voucherProduct.image_url}
            alt={voucherProduct.name}
            className="w-full h-48 object-cover rounded-t-2xl"
          />
        ) : (
          /* Fallback design when no image is uploaded */
          <div className="w-full h-48 flex items-center justify-center bg-gradient-to-br from-evvy-blue to-evvy-dark-blue rounded-t-2xl">
            <div className="text-white text-center px-4">
              <div className="text-2xl font-bold mb-2">🎁</div>
              <div className="text-lg font-semibold">Male Partner</div>
              <div className="text-sm">Treatment Voucher</div>
            </div>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white to-transparent" />
      </div>

      <div className="flex flex-col justify-between flex-grow">
        <div className="px-5 mb-5">
          <div className="text-lg font-medium leading-tight sm:leading-normal mb-1 sm:mb-0">
            Male Partner BV Treatment Voucher
          </div>
          <div className="text-sm text-gray-500">
            Break the cycle of BV recurrence.
          </div>
        </div>
        <div className="px-5 pb-5">
          <button
            className={cn(
              "p-2.5 w-full flex items-center border rounded transition-colors duration-100",
              {
                "border-evvy-dark-beige": inCart,
                "border-evvy-black hover:bg-evvy-blue": !inCart,
              }
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (inCart) {
                openCart?.();
              } else {
                addToCart?.([
                  {
                    item_type: "product",
                    quantity: 1,
                    product_id: voucherProduct.id,
                    ecomm_product: voucherProduct,
                  },
                ]);
              }
            }}
          >
            {inCart && <Checkmark className="h-5 w-5 mr-2 flex-shrink-0" />}
            <div className="flex-grow flex flex-wrap justify-between items-center">
              <div className="t4">{inCart ? "Added" : "Add"}</div>
              <div className="b2 semibold content-stretch">
                $
                {
                  (voucherProduct.discounted_price || voucherProduct.price)
                    .toString()
                    .split(".")[0]
                }
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MalePartnerTreatmentVoucherCard;
